import { useEffect, useState } from 'react';
import { Outlet, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader } from '@/components/Loader';
import { useBrandingColors } from '@/hooks/useBrandingColors';
import { useCustomFavicon } from '@/hooks/useCustomFavicon';
import { useJwt } from '@/hooks/useJwt';
import { useWhitelabelStore } from '@/stores/whitelabel';
import { useListStore } from '@/stores/list';
import { ERouterNames } from '@/types';
import { SESSION_STORAGE_KEYS, LOCAL_STORAGE_KEYS } from '@/utils/constants';
import { LoginContainer } from '@/components/LoginContainer';
import { parseLoginHash } from '@/utils/parseLoginHash';

export const LoginLayout = () => {
  const navigate = useNavigate();
  const isValid = useJwt();

  const {
    setCurrentWhitelabelById,
    setCurrentWhitelabelBySubdomain,
    getInstutitonsWhitelabel,
    currentWhitelabel,
  } = useWhitelabelStore((state) => ({
    setCurrentWhitelabelById: state.setCurrentWhitelabelById,
    setCurrentWhitelabelBySubdomain: state.setCurrentWhitelabelBySubdomain,
    getInstutitonsWhitelabel: state.getInstitutionsWhitelabel,
    currentWhitelabel: state.currentWhitelabel,
  }));

  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const getCountries = useListStore((state) => state.getCountries);

  useEffect(() => {
    const fetchInstitutions = async () => {
      const redirectId = searchParams.get('institutionId');
      const localId = localStorage.getItem(
        LOCAL_STORAGE_KEYS.CURRENT_INSTITUTION_ID,
      );
      const returnPath = searchParams.get(SESSION_STORAGE_KEYS.REDIRECT_PATH);
      if (returnPath) {
        sessionStorage.setItem(SESSION_STORAGE_KEYS.REDIRECT_PATH, returnPath);
      }
      // sso_token / ?error= are handled on the Login page itself (2FA flow / error copy),
      // so here we only react to a real access token from the hash.
      const { token } = parseLoginHash(window.location.hash);
      const email = searchParams.get('email');
      if (token) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token);
      }
      await getInstutitonsWhitelabel(!!token);
      // Forward institutionId to the ROUTE_ME navigation (rather than just
      // consuming it here) so RequiredAuthLayout can still see it - it uses
      // an explicit institutionId as a stronger signal than a remembered
      // last-utilized-organization when resolving which organization to land on.
      const routeMeDestination = redirectId
        ? {
            pathname: ERouterNames.ROUTE_ME,
            search: `institutionId=${redirectId}`,
          }
        : ERouterNames.ROUTE_ME;

      if (token) {
        if (redirectId) setCurrentWhitelabelById(redirectId);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.CURRENT_INSTITUTION_ID);
        navigate(routeMeDestination);
      } else if (isValid && !email) {
        if (redirectId || localId)
          setCurrentWhitelabelById(redirectId ?? localId ?? '');
        navigate(routeMeDestination, { replace: true });
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
        if (redirectId) {
          setCurrentWhitelabelById(redirectId);
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.CURRENT_INSTITUTION_ID,
            redirectId,
          );
        } else {
          const currentSubdomain = window.location.host.split('.')[0];
          setCurrentWhitelabelBySubdomain(currentSubdomain);
        }
      }
      setLoading(false);
    };
    void fetchInstitutions();
  }, [
    getInstutitonsWhitelabel,
    isValid,
    navigate,
    searchParams,
    setCurrentWhitelabelById,
    setCurrentWhitelabelBySubdomain,
  ]);

  useEffect(() => {
    void getCountries();
  }, [getCountries]);

  useBrandingColors(
    currentWhitelabel?.whitelabel?.enabled
      ? currentWhitelabel?.whitelabel?.primaryColor
      : undefined,
    currentWhitelabel?.whitelabel?.enabled
      ? currentWhitelabel?.whitelabel?.buttonTextColor
      : undefined,
  );
  useCustomFavicon(
    currentWhitelabel?.whitelabel?.enabled &&
      currentWhitelabel?.whitelabel?.faviconImageUrl
      ? currentWhitelabel.whitelabel.faviconImageUrl
      : undefined,
  );

  /**
   * It's needed to clear the previous login (where the user logged in from).
   * If they were on the /login/device page and the /me request failed,
   * they should be redirected to /login/device. If we don't have anything stored
   * under the LOCAL_STORAGE_KEYS.LOGIN_SOURCE key in the store, they should be redirected to the /login page.
   */
  useEffect(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.LOGIN_SOURCE);
  }, []);

  if (isValid) {
    return <Loader />;
  }

  return loading ? (
    <Loader />
  ) : (
    <LoginContainer
      as="main"
      background={
        (currentWhitelabel?.whitelabel?.enabled &&
          currentWhitelabel?.whitelabel?.coverImageUrl) ||
        undefined
      }
      mobileLogo={
        (currentWhitelabel?.whitelabel?.enabled &&
          currentWhitelabel?.whitelabel?.mobileHomePageLogoUrl) ||
        undefined
      }
    >
      <Outlet />
    </LoginContainer>
  );
};
